/* eslint-disable no-param-reassign */
import {
    isTransferAllowedForScope,
    validateDescriptionFields,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable
} from '@tassat/forms/src/helpers/validationHelpers';
import { AccountRecord } from '@tassat/fred-definitions';
import { stripPrefix } from '@tassat/utility';

import { AccountAddressDetails, SendAssetFormAction, SendAssetFormState } from './SendAssetFormReducer';
import { IFormErrors, ISendFormValues, SendFormProps } from './types';

export const validateSend = async (
    values: ISendFormValues,
    opts: {
        errors: IFormErrors;
        currentWallet: AccountRecord;
        formState: SendAssetFormState;
        dispatchFormAction: React.Dispatch<SendAssetFormAction>;
        validateWalletAddressWithCore: (
            values: ISendFormValues,
            dispatch: React.Dispatch<SendAssetFormAction>
        ) => Promise<string | AccountAddressDetails>;
        accountPositionBalance: SendFormProps['accountPositionBalance'];
    }
) => {
    const {
        errors,
        currentWallet,
        formState,
        dispatchFormAction,
        validateWalletAddressWithCore,
        accountPositionBalance
    } = opts;
    // TODO If multiple Currency assets, update them with a filter
    const accountPositionCcyBalance = accountPositionBalance[0].ccy[values.AssetType.refData?.currency?.AssetId];
    const accountPositionSecurityBalance = accountPositionBalance[0].security[values.AssetType.refData?.security?.AssetId];

    if (!formState.AccountAddressValidated && formState.AccountAddressDetails.AccountName) {
        dispatchFormAction({ type: 'RESET_ACCOUNT_DETAILS' });
    }

    if (!values.AssetType.value){
        errors.AssetType = 'Please select an asset type';
    }

    if (!values.Purpose.value){
        errors.Purpose = 'Please select a purpose for send or enter an account address';
    }

    if (!values.Recipient && !values.RecipientAddress) {
        errors.Recipient = 'Please select a recipient for send';
    }

    if (values.RecipientAddress !== null){
        if (values.RecipientAddress === currentWallet.Account){
            errors.RecipientAddress = 'Account is invalid or unable to receive';
        }
    }

    const publicDescriptionError = validateDescriptionFields(values.PublicDescription, 'Public Description');
    const publicDescriptionExcelError = validateNoExcelFormulaStartingCharacters(values.PublicDescription);
    const publicDescriptionNonPrintableError = validateNoNonPrintable(values.PublicDescription);

    if (publicDescriptionError) {
        errors.PublicDescription = publicDescriptionError;
    } else if (publicDescriptionExcelError) {
        errors.PublicDescription = publicDescriptionExcelError;
    } else if (publicDescriptionNonPrintableError) {
        errors.PublicDescription = publicDescriptionNonPrintableError;
    }

    const privateDescriptionError = validateDescriptionFields(values.PrivateDescription, 'Private Description');
    const privateDescriptionExcelError = validateNoExcelFormulaStartingCharacters(values.PrivateDescription);
    const privateDescriptionNonPrintableError = validateNoNonPrintable(values.PrivateDescription);
    if (privateDescriptionError) {
        errors.PrivateDescription = privateDescriptionError;
    } else if (privateDescriptionExcelError) {
        errors.PrivateDescription = privateDescriptionExcelError;
    } else if (privateDescriptionNonPrintableError) {
        errors.PrivateDescription = privateDescriptionNonPrintableError;
    }

    if (values.RecipientAddress && values.RecipientAddress !== currentWallet.Account) {
        if (!formState.AccountAddressValidated) {
            try {
                const coreResponse = await validateWalletAddressWithCore(
                    values,
                    dispatchFormAction
                );
                if (typeof coreResponse === 'string') {
                    errors.RecipientAddress = coreResponse;
                    dispatchFormAction({
                        type: 'SERVER_ERROR',
                        payload: { error: coreResponse }
                    });
                } else if (typeof coreResponse === 'object') {
                    dispatchFormAction({
                        type: 'SERVER_SUCCESS',
                        payload: { AccountAddressDetails: coreResponse as AccountAddressDetails }
                    });
                }
            } catch (error) {
                errors.RecipientAddress = 'Could not validate recipient address, please try again';
            }
        } else if (formState.AccountAddressValidated && formState.AccountAddressError) {
            errors.RecipientAddress = formState.AccountAddressError;
        }
    }

    if (values.AssetType.refData?.security) {
        const securityValue = Number(stripPrefix(values.AssetAmount, `${values.AssetType.refData?.security.Ticker} `)
            .replace(/,/g, ''));

        if (!securityValue) {
            errors.AssetAmount = 'Please enter an amount to send';
        }

        if (securityValue > Number(accountPositionSecurityBalance?.availableBalance ?? 0)) {
            errors.AssetAmount = `Amount entered exceeds your available ${values.AssetType.refData.security.Ticker} balance`;
        }
    }

    if (!values.AssetType.refData?.security && values.AssetType.refData?.currency){
        const currencyValue = Number(stripPrefix(values.FiatAmount, '$')
            .replace(/,/g, ''));

        if (!currencyValue) {
            errors.FiatAmount = 'Please enter an amount to send';
        }

        if (currencyValue > Number(accountPositionCcyBalance?.availableBalance ?? 0)) {
            errors.FiatAmount = `Amount entered exceeds your available ${values.AssetType.refData.currency.Ticker} balance`;
        }
    }

    const selectedAsset = values.AssetType.refData?.security ?? values.AssetType.refData?.currency;
    const isSameFirm = (firmId: string) => firmId === currentWallet.FirmId;

    if (values.Recipient !== null &&
        !isTransferAllowedForScope(selectedAsset?.TransferScope, isSameFirm(values.Recipient.value.FirmId))) {
        errors.RecipientAddress = 'Account is invalid or unable to receive';
    }

    if (formState.AccountAddressDetails && formState.AccountAddressValidated &&
        !isTransferAllowedForScope(selectedAsset?.TransferScope, isSameFirm(formState.AccountAddressDetails.FirmId))) {
        errors.RecipientAddress = 'Account is invalid or unable to receive';
    }

    return errors;
};